import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '@/hooks/useAuth';
import { BackupModalProvider } from '@/hooks/useBackupModal';
import BackupButton from './BackupButton';
import { todayMexicoLong } from '@/lib/dates';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || 'JD')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <BackupModalProvider>
      <div className="flex h-screen overflow-hidden bg-background-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="h-16 flex items-center justify-between px-6 bg-white border-b border-background-200 flex-shrink-0 z-30">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-calendar-line text-foreground-400"></i>
                <span>
                  {todayMexicoLong()}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <BackupButton />
              <div className="relative">
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative w-9 h-9 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-notification-3-line text-foreground-600 text-lg"></i>
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">3</span>
                </button>
                {notificationsOpen && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl border border-background-200 shadow-2xl z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-background-100">
                      <span className="text-sm font-semibold text-foreground-800">Notificaciones</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {[
                        { icon: 'ri-error-warning-line', color: 'text-red-500', bg: 'bg-red-50', title: 'Factura vencida', desc: 'Transportes Norte - Folio FAC-2024-089', time: 'Hace 2 horas' },
                        { icon: 'ri-timer-line', color: 'text-amber-500', bg: 'bg-amber-50', title: 'Próximo vencimiento', desc: 'Logística Express - Crédito vence en 3 días', time: 'Hace 5 horas' },
                        { icon: 'ri-check-double-line', color: 'text-green-500', bg: 'bg-green-50', title: 'Pago registrado', desc: 'Seguridad Privada SA - $45,800 MXN', time: 'Hace 1 día' },
                      ].map((notif, i) => (
                        <div key={i} className="flex gap-3 px-4 py-3 hover:bg-background-50 cursor-pointer transition-colors border-b border-background-100 last:border-0">
                          <div className={`w-8 h-8 rounded-lg ${notif.bg} flex items-center justify-center flex-shrink-0`}>
                            <i className={`${notif.icon} ${notif.color} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground-800">{notif.title}</p>
                            <p className="text-xs text-foreground-500 truncate">{notif.desc}</p>
                            <p className="text-[10px] text-foreground-400 mt-0.5">{notif.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 pl-3 border-l border-background-200">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{initials}</span>
                </div>
                <div className="hidden sm:flex flex-col leading-tight">
                  <span className="text-xs font-semibold text-foreground-800">{user?.name || 'Usuario'}</span>
                  <span className="text-[10px] text-foreground-500">Admin</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-1 w-9 h-9 rounded-lg bg-background-100 hover:bg-red-50 flex items-center justify-center cursor-pointer transition-colors group"
                  title="Cerrar Sesión"
                >
                  <i className="ri-logout-box-r-line text-foreground-500 group-hover:text-red-500 transition-colors"></i>
                </button>
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-y-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </BackupModalProvider>
  );
}